import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  User, Briefcase, Award, Globe, MessageSquare, Video, 
  ArrowRight, ArrowLeft, Check, Sparkles, Loader2, Upload,
  Linkedin, Twitter, Mail, Phone, Building2, Target, Eye,
  BookOpen, Mic, Trophy, Lightbulb, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LeadershipProfileCreate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [formData, setFormData] = useState({
    leadership_mode: 'company_leadership',
    leader_name: '',
    leader_title: '',
    leader_photo: '',
    tagline: '',
    company_name: '',
    company_role: '',
    vision_statement: '',
    leadership_philosophy: '',
    career_journey: '',
    years_experience: '',
    expertise_areas: [],
    industries: [],
    achievements: '',
    awards: [],
    certifications: [],
    thought_leadership_topics: [],
    profile_sections: null,
    hero_video: null,
    additional_videos: [],
    contact_email: user?.email || '',
    contact_phone: '',
    linkedin_url: '',
    twitter_url: '',
    website: ''
  });

  const steps = formData.leadership_mode === 'company_leadership' 
    ? ['Mode', 'Basics', 'Company Role', 'Expertise', 'Vision', 'Contact', 'Preview']
    : ['Mode', 'Basics', 'Career', 'Expertise', 'Thought Leadership', 'Contact', 'Preview'];

  const currentStepName = steps[currentStep];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleArrayInput = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, [field]: items }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    const photoFormData = new FormData();
    photoFormData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/upload/image`, photoFormData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      handleInputChange('leader_photo', response.data.url);
    } catch (err) {
      setError('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const generateSections = async () => {
    setIsGenerating(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/leadership-profiles/generate-sections`, {
        leader_name: formData.leader_name,
        leader_title: formData.leader_title,
        company_name: formData.company_name,
        leadership_mode: formData.leadership_mode,
        vision_statement: formData.vision_statement,
        career_journey: formData.career_journey,
        expertise_areas: formData.expertise_areas,
        achievements: formData.achievements
      }, { withCredentials: true });
      
      handleInputChange('profile_sections', response.data.profile_sections);
    } catch (err) {
      setError('Failed to generate profile sections');
    } finally {
      setIsGenerating(false);
    }
  };

  const validateStep = () => {
    switch (currentStepName) {
      case 'Basics':
        if (!formData.leader_name.trim()) {
          setError('Please enter your name');
          return false;
        }
        if (!formData.leader_title.trim()) {
          setError('Please enter your title');
          return false;
        }
        return true;
      case 'Company Role':
        if (formData.leadership_mode === 'company_leadership' && !formData.company_name.trim()) {
          setError('Please enter your company name');
          return false;
        }
        return true;
      case 'Contact':
        if (!formData.contact_email.trim()) {
          setError('Please enter your email');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = async () => {
    if (!validateStep()) return;
    
    // Generate AI sections before preview
    if (currentStep === steps.length - 2 && !formData.profile_sections) {
      await generateSections();
    }
    
    setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/leadership-profiles`, formData, {
        withCredentials: true
      });
      
      navigate(`/l/${response.data.slug}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create profile');
    } finally {
      setIsSaving(false);
    }
  };

  const expertiseOptions = [
    'Strategy', 'Operations', 'Finance', 'Marketing', 'Sales', 'Technology',
    'Product', 'HR', 'Legal', 'Supply Chain', 'R&D', 'Innovation',
    'Digital Transformation', 'Sustainability', 'M&A', 'International Business'
  ];

  const industryOptions = [
    'Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 
    'Energy', 'Pharma', 'FMCG', 'Automotive', 'Real Estate', 'Education',
    'Media', 'Telecommunications', 'Agriculture', 'Logistics'
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Create Leadership Profile</h1>
          <p className="text-zinc-400">Build your professional story</p>
          
          {/* Profile Type Switch */}
          <div className="mt-4 flex items-center justify-center gap-2 text-sm">
            <span className="text-zinc-500">Looking to create a company profile?</span>
            <a 
              href="/profile/create" 
              className="text-[#C41E3A] hover:text-[#ff4d6a] font-medium"
            >
              Create Company Profile →
            </a>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8 flex-wrap">
          {steps.map((step, idx) => (
            <div key={step} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                idx < currentStep ? 'bg-[#C41E3A] text-white' :
                idx === currentStep ? 'bg-[#C41E3A] text-white ring-2 ring-[#C41E3A]/50' :
                'bg-zinc-800 text-zinc-500'
              }`}>
                {idx < currentStep ? <Check className="w-4 h-4" /> : idx + 1}
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-8 h-0.5 ${idx < currentStep ? 'bg-[#C41E3A]' : 'bg-zinc-800'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Form Container */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 md:p-8">
          
          {/* Step: Mode Selection */}
          {currentStepName === 'Mode' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Target className="w-5 h-5 text-[#C41E3A]" />
                Choose Your Profile Type
              </h2>
              <p className="text-zinc-400">Select how you want to present your leadership story</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleInputChange('leadership_mode', 'company_leadership')}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    formData.leadership_mode === 'company_leadership'
                      ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <Building2 className="w-8 h-8 text-[#C41E3A] mb-3" />
                  <h3 className="text-lg font-semibold mb-2">Company Leadership</h3>
                  <p className="text-zinc-400 text-sm">
                    Highlight your role and impact within your organization. 
                    Perfect for corporate profiles and team pages.
                  </p>
                  <ul className="mt-3 text-xs text-zinc-500 space-y-1">
                    <li>• Your role in the company</li>
                    <li>• Team and organizational impact</li>
                    <li>• Company-focused vision</li>
                  </ul>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleInputChange('leadership_mode', 'leadership_identity')}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    formData.leadership_mode === 'leadership_identity'
                      ? 'border-[#C41E3A] bg-[#C41E3A]/10'
                      : 'border-zinc-700 hover:border-zinc-600'
                  }`}
                >
                  <User className="w-8 h-8 text-[#C41E3A] mb-3" />
                  <h3 className="text-lg font-semibold mb-2">Leadership Identity</h3>
                  <p className="text-zinc-400 text-sm">
                    Build your personal brand and thought leadership presence.
                    Ideal for speakers, consultants, and industry experts.
                  </p>
                  <ul className="mt-3 text-xs text-zinc-500 space-y-1">
                    <li>• Career journey and expertise</li>
                    <li>• Thought leadership topics</li>
                    <li>• Personal brand building</li>
                  </ul>
                </button>
              </div>
            </div>
          )}

          {/* Step: Basics */}
          {currentStepName === 'Basics' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <User className="w-5 h-5 text-[#C41E3A]" />
                Your Details
              </h2>
              
              {/* Photo Upload */}
              <div className="flex items-center gap-6">
                <div 
                  onClick={() => photoInputRef.current?.click()}
                  className="w-24 h-24 rounded-full bg-zinc-800 border-2 border-dashed border-zinc-700 flex items-center justify-center cursor-pointer hover:border-[#C41E3A] transition-colors overflow-hidden"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                  ) : formData.leader_photo ? (
                    <img src={formData.leader_photo} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <Upload className="w-6 h-6 text-zinc-500" />
                  )}
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <div>
                  <p className="text-sm font-medium">Profile Photo</p>
                  <p className="text-xs text-zinc-500">Click to upload (optional)</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.leader_name}
                    onChange={(e) => handleInputChange('leader_name', e.target.value)}
                    placeholder="John Doe"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  />
                </div>
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    Title / Designation *
                  </label>
                  <input
                    type="text"
                    value={formData.leader_title}
                    onChange={(e) => handleInputChange('leader_title', e.target.value)}
                    placeholder="CEO & Founder"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Tagline (Optional)
                </label>
                <input
                  type="text"
                  value={formData.tagline}
                  onChange={(e) => handleInputChange('tagline', e.target.value)}
                  placeholder="Transforming industries through innovation"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Years of Experience
                </label>
                <input
                  type="text"
                  value={formData.years_experience}
                  onChange={(e) => handleInputChange('years_experience', e.target.value)}
                  placeholder="e.g., 20+ years"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                />
              </div>
            </div>
          )}

          {/* Step: Company Role (for company_leadership mode) */}
          {currentStepName === 'Company Role' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#C41E3A]" />
                Your Company Role
              </h2>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Acme Corporation"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Your Role Description
                </label>
                <textarea
                  value={formData.company_role}
                  onChange={(e) => handleInputChange('company_role', e.target.value)}
                  placeholder="Describe your responsibilities and scope within the organization..."
                  rows={4}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Key Achievements in This Role
                </label>
                <textarea
                  value={formData.achievements}
                  onChange={(e) => handleInputChange('achievements', e.target.value)}
                  placeholder="Revenue growth, team building, market expansion, product launches..."
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                />
              </div>
            </div>
          )}

          {/* Step: Career (for leadership_identity mode) */}
          {currentStepName === 'Career' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-[#C41E3A]" />
                Your Career Journey
              </h2>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Career Narrative
                </label>
                <textarea
                  value={formData.career_journey}
                  onChange={(e) => handleInputChange('career_journey', e.target.value)}
                  placeholder="Share your career journey - how you started, key transitions, defining moments..."
                  rows={5}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Key Achievements
                </label>
                <textarea
                  value={formData.achievements}
                  onChange={(e) => handleInputChange('achievements', e.target.value)}
                  placeholder="Major milestones, companies built, teams led, impact created..."
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Current/Recent Company (Optional)
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Company name"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                />
              </div>
            </div>
          )}

          {/* Step: Expertise */}
          {currentStepName === 'Expertise' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Award className="w-5 h-5 text-[#C41E3A]" />
                Expertise & Skills
              </h2>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-3">
                  Areas of Expertise
                </label>
                <div className="flex flex-wrap gap-2">
                  {expertiseOptions.map(exp => (
                    <button
                      key={exp}
                      type="button"
                      onClick={() => {
                        const current = formData.expertise_areas;
                        if (current.includes(exp)) {
                          handleInputChange('expertise_areas', current.filter(e => e !== exp));
                        } else {
                          handleInputChange('expertise_areas', [...current, exp]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.expertise_areas.includes(exp)
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {exp}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-3">
                  Industries
                </label>
                <div className="flex flex-wrap gap-2">
                  {industryOptions.map(ind => (
                    <button
                      key={ind}
                      type="button"
                      onClick={() => {
                        const current = formData.industries;
                        if (current.includes(ind)) {
                          handleInputChange('industries', current.filter(i => i !== ind));
                        } else {
                          handleInputChange('industries', [...current, ind]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                        formData.industries.includes(ind)
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {ind}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Certifications (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.certifications.join(', ')}
                  onChange={(e) => handleArrayInput('certifications', e.target.value)}
                  placeholder="MBA, CFA, PMP, Six Sigma..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                />
              </div>
            </div>
          )}

          {/* Step: Vision (for company_leadership) */}
          {currentStepName === 'Vision' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-[#C41E3A]" />
                Vision & Leadership Philosophy
              </h2>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Your Vision
                </label>
                <textarea
                  value={formData.vision_statement}
                  onChange={(e) => handleInputChange('vision_statement', e.target.value)}
                  placeholder="What's your vision for the company/industry? What drives you?"
                  rows={4}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Leadership Philosophy (Optional)
                </label>
                <textarea
                  value={formData.leadership_philosophy}
                  onChange={(e) => handleInputChange('leadership_philosophy', e.target.value)}
                  placeholder="How do you lead? What principles guide your leadership style?"
                  rows={3}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                />
              </div>
            </div>
          )}

          {/* Step: Thought Leadership (for leadership_identity) */}
          {currentStepName === 'Thought Leadership' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#C41E3A]" />
                Thought Leadership
              </h2>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Your Vision & Perspective
                </label>
                <textarea
                  value={formData.vision_statement}
                  onChange={(e) => handleInputChange('vision_statement', e.target.value)}
                  placeholder="Your unique perspective on industry trends, future of work, etc."
                  rows={4}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Topics You Speak/Write About (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.thought_leadership_topics.join(', ')}
                  onChange={(e) => handleArrayInput('thought_leadership_topics', e.target.value)}
                  placeholder="Digital Transformation, Future of Work, Leadership..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                />
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  Awards & Recognition (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.awards.join(', ')}
                  onChange={(e) => handleArrayInput('awards', e.target.value)}
                  placeholder="40 Under 40, Industry Leader Award..."
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                />
              </div>
            </div>
          )}

          {/* Step: Contact */}
          {currentStepName === 'Contact' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#C41E3A]" />
                Contact Information
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e.target.value)}
                    placeholder="you@company.com"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  />
                </div>
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    Phone (Optional)
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => handleInputChange('contact_phone', e.target.value)}
                    placeholder="+91 98765 43210"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-zinc-300 text-sm font-medium mb-2">
                  <Linkedin className="w-4 h-4 inline mr-1" />
                  LinkedIn URL
                </label>
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                  placeholder="https://linkedin.com/in/yourprofile"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <Twitter className="w-4 h-4 inline mr-1" />
                    Twitter/X URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={formData.twitter_url}
                    onChange={(e) => handleInputChange('twitter_url', e.target.value)}
                    placeholder="https://twitter.com/yourhandle"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  />
                </div>
                <div>
                  <label className="block text-zinc-300 text-sm font-medium mb-2">
                    <Globe className="w-4 h-4 inline mr-1" />
                    Website (Optional)
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourwebsite.com"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/50"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step: Preview */}
          {currentStepName === 'Preview' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Eye className="w-5 h-5 text-[#C41E3A]" />
                Preview Your Profile
              </h2>
              
              {isGenerating ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#C41E3A] mx-auto mb-4" />
                  <p className="text-zinc-400">Generating your profile...</p>
                </div>
              ) : (
                <div className="bg-zinc-800 rounded-xl p-6">
                  {/* Preview Header */}
                  <div className="flex items-start gap-4 mb-6">
                    {formData.leader_photo ? (
                      <img src={formData.leader_photo} alt={formData.leader_name} className="w-20 h-20 rounded-full object-cover" />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#C41E3A] to-[#8a1428] flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">
                          {formData.leader_name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-bold">{formData.leader_name || 'Your Name'}</h3>
                      <p className="text-[#C41E3A]">{formData.leader_title || 'Your Title'}</p>
                      {formData.company_name && (
                        <p className="text-zinc-400 text-sm">{formData.company_name}</p>
                      )}
                      {formData.tagline && (
                        <p className="text-zinc-300 text-sm mt-1 italic">{formData.tagline}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* AI Sections */}
                  {formData.profile_sections && (
                    <div className="space-y-4">
                      {formData.profile_sections.who_i_am && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#C41E3A] mb-1">Who I Am</h4>
                          <p className="text-zinc-300 text-sm">{formData.profile_sections.who_i_am}</p>
                        </div>
                      )}
                      {formData.profile_sections.my_journey && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#C41E3A] mb-1">My Journey</h4>
                          <p className="text-zinc-300 text-sm">{formData.profile_sections.my_journey}</p>
                        </div>
                      )}
                      {formData.profile_sections.my_expertise && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#C41E3A] mb-1">My Expertise</h4>
                          <p className="text-zinc-300 text-sm">{formData.profile_sections.my_expertise}</p>
                        </div>
                      )}
                      {formData.profile_sections.my_vision && (
                        <div>
                          <h4 className="text-sm font-semibold text-[#C41E3A] mb-1">My Vision</h4>
                          <p className="text-zinc-300 text-sm">{formData.profile_sections.my_vision}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Expertise Tags */}
                  {formData.expertise_areas.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-zinc-700">
                      <h4 className="text-xs font-semibold text-zinc-500 mb-2">EXPERTISE</h4>
                      <div className="flex flex-wrap gap-2">
                        {formData.expertise_areas.map(exp => (
                          <span key={exp} className="px-2 py-1 bg-zinc-700 rounded text-xs">{exp}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <button
                onClick={generateSections}
                disabled={isGenerating}
                className="w-full flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white py-3 rounded-lg transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                Regenerate AI Content
              </button>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-zinc-800">
            {currentStep > 0 ? (
              <button
                onClick={prevStep}
                className="flex items-center gap-2 px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <div />
            )}
            
            {currentStep < steps.length - 1 ? (
              <button
                onClick={nextStep}
                disabled={isGenerating}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#C41E3A] hover:bg-[#A01830] rounded-lg transition-colors disabled:opacity-50"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSaving || isGenerating}
                className="flex items-center gap-2 px-8 py-2.5 bg-[#C41E3A] hover:bg-[#A01830] rounded-lg transition-colors disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Create Profile
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
